import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVendorAuth } from './VendorAuthProvider';
import VendorDashboardLayout from './VendorDashboardLayout';
import { getVendorByUid, addOtherDocument, getOtherDocuments, deleteOtherDocument, OtherDocument } from '../firebaseService';
import { Building, CheckCircle, AlertCircle, FileText, Loader2, Upload, X, Download, Trash2, Plus } from 'lucide-react';

const VendorDashboardView: React.FC = () => {
  const navigate = useNavigate();
  const { vendor } = useVendorAuth();
  const [vendorData, setVendorData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Other Documents state
  const [otherDocuments, setOtherDocuments] = useState<OtherDocument[]>([]);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  useEffect(() => {
    const loadVendorData = async () => {
      if (vendor?.uid) {
        try {
          const result = await getVendorByUid(vendor.uid);
          if (result.success && result.vendor) {
            setVendorData(result.vendor);
          }
          
          // Load other documents
          const docsResult = await getOtherDocuments(vendor.uid);
          if (docsResult.success && docsResult.documents) {
            setOtherDocuments(docsResult.documents);
          }
        } catch (error) {
          console.error('Error loading vendor data:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadVendorData();
  }, [vendor]);

  if (loading) {
    return (
      <VendorDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin h-8 w-8 text-sunbeth-orange-600" />
        </div>
      </VendorDashboardLayout>
    );
  }

  const profileComplete = vendorData?.profileComplete || false;
  
  // Helper functions to check if sections are actually completed
  const isCategoriesCompleted = () => {
    if (!vendorData?.categories) return false;
    // Check if at least one category is selected (excluding othersSpecify)
    return Object.entries(vendorData.categories).some(([key, value]) => 
      key !== 'othersSpecify' && value === true
    ) && !!vendorData.specializationDetails?.trim() && !!vendorData.paymentPlan?.trim();
  };

  const isBankingCompleted = () => {
    if (!vendorData?.bankingInfo || !Array.isArray(vendorData.bankingInfo)) return false;
    // Check if at least one bank entry has all required fields
    return vendorData.bankingInfo.some((bank: any) => 
      bank?.bankName?.trim() && bank?.accountType?.trim() && bank?.accountNumber?.trim()
    );
  };

  const isDocumentsRegistrationCompleted = () => {
    if (!vendorData?.documentUrls || typeof vendorData.documentUrls !== 'object') return false;
    // Check if at least one required registration document is uploaded
    const requiredDocs = [
      'applicationLetter',
      'incorporationCert',
      'vatCert',
      'passportPhoto',
      'previousJobEvidence',
      'memat',
      'identityDocuments',
      'utilityBill',
      'cacStatusReport'
    ];
    return requiredDocs.some(doc => vendorData.documentUrls[doc]);
  };

  const isDocumentsComplianceCompleted = () => {
    if (!vendorData?.documentUrls || typeof vendorData.documentUrls !== 'object') return false;
    // Check if at least one compliance document is uploaded (all are optional, so we check if any exists)
    const complianceDocs = [
      'performanceBond',
      'exportImportLicense',
      'iso9001Certificate',
      'productComplianceCertificates',
      'hsePolicy',
      'safetyRecords',
      'generalLiabilityInsurance',
      'productLiabilityInsurance',
      'supplierCodeOfConduct',
      'antiBriberyPolicyAcknowledgment',
      'dataProtectionPolicy'
    ];
    // Since these are all optional, we'll mark as completed if at least one is uploaded
    // or if the registration documents are completed (indicating user has been through the process)
    return complianceDocs.some(doc => vendorData.documentUrls[doc]) || isDocumentsRegistrationCompleted();
  };

  const completionSteps = [
    { id: 'profile', label: 'Business Details', path: '/vendor/dashboard/profile', completed: !!vendorData?.companyName?.trim() },
    { id: 'categories', label: 'Categories & Services', path: '/vendor/dashboard/categories', completed: isCategoriesCompleted() },
    { id: 'banking', label: 'Banking & References', path: '/vendor/dashboard/banking', completed: isBankingCompleted() },
    { id: 'documents-registration', label: 'Business Registration & Identity', path: '/vendor/dashboard/documents-registration', completed: isDocumentsRegistrationCompleted() },
    { id: 'documents-compliance', label: 'Compliance & Certifications', path: '/vendor/dashboard/documents-compliance', completed: isDocumentsComplianceCompleted() },
    { id: 'contact', label: 'Contact & Declaration', path: '/vendor/dashboard/contact', completed: !!vendorData?.authorizedRepresentativeName?.trim() && !!vendorData?.authorizedRepresentativePhone?.trim() && !!vendorData?.authorizedRepresentativeDesignation?.trim() && !!vendorData?.contactPersonName?.trim() && !!vendorData?.contactPersonPhone?.trim() && !!vendorData?.contactPersonEmail?.trim() && vendorData?.declarationAccepted === true },
  ];

  const completedCount = completionSteps.filter(step => step.completed).length;

  // Handle adding other document
  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendor?.uid || !documentName.trim() || !documentFile) {
      setUploadError('Please provide both document name and file.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const result = await addOtherDocument(vendor.uid, documentName, documentFile);
      if (result.success && result.document) {
        setOtherDocuments(prev => [...prev, result.document!]);
        setDocumentName('');
        setDocumentFile(null);
        setShowAddDocument(false);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      } else {
        setUploadError(result.error || 'Failed to upload document');
      }
    } catch (error: any) {
      setUploadError(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Handle deleting other document
  const handleDeleteDocument = async (documentId: string) => {
    if (!vendor?.uid || !confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeletingDocId(documentId);
    try {
      const result = await deleteOtherDocument(vendor.uid, documentId);
      if (result.success) {
        setOtherDocuments(prev => prev.filter(doc => doc.id !== documentId));
      } else {
        alert(result.error || 'Failed to delete document');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to delete document');
    } finally {
      setDeletingDocId(null);
    }
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

    return (
    <VendorDashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Welcome, {vendor?.displayName || vendor?.email || 'Vendor'}!
          </h2>
          <p className="text-gray-600">
            Complete your vendor profile to get started with Sunbeth Global Concepts Limited.
          </p>
        </div>

        {/* Profile Completion Status */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Profile Completion</h2>
            <span className="text-sm font-medium text-gray-600">
              {completedCount} of {completionSteps.length} completed
            </span>
      </div>
          
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-sunbeth-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedCount / completionSteps.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="space-y-3">
            {completionSteps.map((step) => {
              const Icon = step.completed ? CheckCircle : AlertCircle;
              return (
              <button
                  key={step.id}
                  onClick={() => navigate(step.path)}
                  className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <Icon
                      className={`h-5 w-5 mr-3 ${
                        step.completed ? 'text-green-500' : 'text-gray-400'
                      }`}
                    />
                    <span className="text-sm font-medium text-gray-700">{step.label}</span>
                  </div>
                  {!step.completed && (
                    <span className="text-xs text-sunbeth-orange-600 font-medium">Complete →</span>
                  )}
              </button>
              );
            })}
        </div>
      </div>

        {/* Quick Info */}
        {vendorData && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center mb-4 text-left">
                <Building className="h-6 w-6 text-sunbeth-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 text-left">Company Information</h3>
              </div>
              <div className="space-y-2 text-sm text-left">
                <div className="text-left">
                  <span className="text-gray-500">Company:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {vendorData.companyName || 'Not set'}
                  </span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">Email:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {vendorData.email || vendor?.email || 'Not set'}
                  </span>
                </div>
                <div className="text-left">
                  <span className="text-gray-500">Phone:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {vendorData.telephone || 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center mb-4 text-left">
                <FileText className="h-6 w-6 text-sunbeth-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 text-left">Status</h3>
              </div>
              <div className="space-y-2 text-left">
                <div className="flex items-center text-left">
                  <span className="text-sm text-gray-500">Profile Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                    profileComplete
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {profileComplete ? 'Complete' : 'In Progress'}
                  </span>
                </div>
                {vendorData?.status && (
                  <div className="flex items-center text-left">
                    <span className="text-sm text-gray-500">Application Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      vendorData.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : vendorData.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {vendorData.status.charAt(0).toUpperCase() + vendorData.status.slice(1)}
                        </span>
        </div>
      )}
            </div>
          </div>
        </div>
      )}

        {/* Other Documents Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <FileText className="h-6 w-6 text-sunbeth-orange-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 text-left">Other Documents</h2>
            </div>
            <button
              onClick={() => setShowAddDocument(!showAddDocument)}
              className="flex items-center px-4 py-2 bg-sunbeth-orange-600 text-white rounded-md hover:bg-sunbeth-orange-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Document
            </button>
          </div>

          {/* Success Message */}
          {uploadSuccess && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                <p className="text-sm text-green-700">Document uploaded successfully!</p>
              </div>
            </div>
          )}

          {/* Add Document Form */}
          {showAddDocument && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <form onSubmit={handleAddDocument} className="space-y-4">
                <div>
                  <label htmlFor="document-name" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Document Name
                  </label>
                  <input
                    id="document-name"
                    type="text"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-sunbeth-orange-500 focus:border-sunbeth-orange-500"
                    placeholder="e.g., Insurance Certificate, License Renewal"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="document-file" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Document File (Max 5 MB)
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      id="document-file"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            setUploadError('File size exceeds 5 MB limit.');
                            setDocumentFile(null);
                          } else {
                            setDocumentFile(file);
                            setUploadError('');
                          }
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-sunbeth-orange-50 file:text-sunbeth-orange-700 hover:file:bg-sunbeth-orange-100"
                      required
                    />
                    {documentFile && (
                      <span className="text-sm text-gray-600">{documentFile.name}</span>
                    )}
                  </div>
                </div>

                {uploadError && (
                  <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-700">{uploadError}</p>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={uploading || !documentName.trim() || !documentFile}
                    className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                      uploading || !documentName.trim() || !documentFile
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'bg-sunbeth-orange-600 text-white hover:bg-sunbeth-orange-700'
                    } transition-colors`}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddDocument(false);
                      setDocumentName('');
                      setDocumentFile(null);
                      setUploadError('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Documents List */}
          {otherDocuments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-sm">No other documents uploaded yet.</p>
              <p className="text-xs mt-1">Click "Add Document" to upload your first document.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {otherDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-sunbeth-orange-600 mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate text-left">{doc.name}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-xs text-gray-500 truncate">{doc.fileName}</p>
                        <span className="text-xs text-gray-400">•</span>
                        <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                        <span className="text-xs text-gray-400">•</span>
                        <p className="text-xs text-gray-500">{formatDate(doc.uploadedAt)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-sunbeth-orange-600 hover:bg-sunbeth-orange-50 rounded-md transition-colors"
                      title="Download"
                    >
                      <Download className="h-5 w-5" />
                    </a>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      disabled={deletingDocId === doc.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {deletingDocId === doc.id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Trash2 className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </VendorDashboardLayout>
  );
};

export default VendorDashboardView;
